// src/components/post_course/PostCourseContainer.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import * as EnrollmentAPI from "../../util/enrollment_api_util";
import PostCourseViewer from "./PostCourseViewer";
import { POST_COURSE_MODULES_BY_KEY } from "../dashboard/dashboard"; // consider moving to its own file later

const COURSE_EXTENSION_PAYMENT_LINK = "https://square.link/u/VBauMzNW";

const getCourseKeyFromEnrollment = (enr) => {
  const c = enr?.course || {};
  return c.key || c.courseKey || enr?.courseKey || "";
};

const titleFromKey = (key) => {
  if (!key) return "Post-Course";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const isExpiredEnrollment = (enrollment) => {
  if (!enrollment) return false;
  if (String(enrollment?.status || "").toLowerCase() === "expired") return true;
  const expiry = enrollment?.expiryDate ? new Date(enrollment.expiryDate) : null;
  return !!expiry && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
};

const formatDateUTC = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { timeZone: "UTC" });
};

export default function PostCourseContainer() {
  const { courseKey } = useParams();

  // ✅ Pull the logged-in user from Redux so PostCourseViewer can display the artist name
  const currentUser = useSelector((state) => state?.session?.user || null);

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await EnrollmentAPI.fetchMyEnrollments();

      // ✅ Support either:
      // - res.data being an array
      // - res.data.enrollments being an array
      const data = res?.data;
      const arr = Array.isArray(data) ? data : Array.isArray(data?.enrollments) ? data.enrollments : [];

      setEnrollments(arr);
    } catch (e) {
      console.error("Failed to fetch enrollments:", e);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!currentUser) return;

    const socket = io({ withCredentials: true });
    const currentId = String(currentUser?._id || currentUser?.id || "");

    if (currentId) {
      socket.emit("join", { userId: currentId, role: String(currentUser?.role || "") });
    }

    const refreshIfMine = (payload) => {
      const userId = String(payload?.userId || payload?.studentId || payload?._id || "");
      if (!currentId || !userId) return;
      if (currentId !== userId) return;
      refresh();
    };

    socket.on("enrollment:updated", refreshIfMine);
    socket.on("postcourse:reviewed", refreshIfMine);
    socket.on("postcourse:submitted", refreshIfMine);
    socket.on("postcourse:reviewed:student", refreshIfMine);
    socket.on("postcourse:submitted:student", refreshIfMine);

    return () => socket.disconnect();
  }, [currentUser, refresh]);

  const enrollment = useMemo(() => {
    return (enrollments || []).find((enr) => getCourseKeyFromEnrollment(enr) === courseKey) || null;
  }, [enrollments, courseKey]);
  const enrollmentExpired = isExpiredEnrollment(enrollment);

  const module = POST_COURSE_MODULES_BY_KEY?.[courseKey] || null;

  // ✅ Mark-finished handler (no-assignment sections)
  const markSectionPassed = useCallback(async (enrollmentId, sectionId) => {
    return EnrollmentAPI.markPostCourseSectionPassed(enrollmentId, sectionId);
  }, []);

  if (enrollmentExpired) {
    return (
      <div className="ink-messages-shell course-shell">
        <div className="master-messages">
          <div className="master-messages-chat course-main">
            <div className="empty-chat-state lesson-empty-state">
              <h3>Course access expired</h3>
              <p className="muted">
                Your post-course progress has been saved. Once your 3-month extension is purchased, your master can
                reopen access for you.
              </p>
              {!!enrollment?.expiryDate && (
                <p className="muted">Access ended on {formatDateUTC(enrollment.expiryDate)}.</p>
              )}
              <a
                href={COURSE_EXTENSION_PAYMENT_LINK}
                target="_blank"
                rel="noreferrer"
                className="dash-course-cta dash-course-cta-secondary"
                style={{ marginTop: 18 }}
              >
                Extend Access 3 Months — $150
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PostCourseViewer
      course={{
        key: courseKey,
        title: titleFromKey(courseKey),
      }}
      currentUser={currentUser}
      enrollment={enrollment}
      module={module}
      onSubmitSectionAssignment={EnrollmentAPI.submitPostCourseAssignment}
      onReviewSectionAssignment={EnrollmentAPI.reviewPostCourseAssignment}
      onMarkSectionPassed={markSectionPassed} // ✅ new prop
      onSubmitPostCourseQuizAttempt={EnrollmentAPI.submitPostCourseQuizAttempt}
      onRefreshEnrollments={refresh}
      isLoading={loading} // harmless extra prop if your viewer ignores it
    />
  );
}
